import { Localized, useLocalization } from "@fluent/react"
import AddIcon from "@mui/icons-material/Add"
import ClearIcon from "@mui/icons-material/Clear"
import SearchIcon from "@mui/icons-material/Search"
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Paper, Skeleton, Stack, Switch, Tooltip, Typography, } from "@mui/material"
import { useCallback, useEffect, useMemo, useState } from "react"
import { AppRequiredMessage } from "../../../app/AppRequiredMessage"
import { AppId } from "../../../app/appTypes"
import { CancelButton } from "../../../app/components/CancelButton"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { CreateSupplierDialog } from "../dialogs/CreateSupplierDialog"
import { loadStructureSuppliers, loadSuppliers, selectAllSuppliers, selectSuppliers, Supplier, } from "../suppliersSlice"
import { DataGrid, GridColDef, GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales";
import { selectSettings } from "../../settings/settingsSlice"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"

export interface SuppliersSelectorProps {
    selectedStructureId: AppId | null
    value: string
    showFiltersTab?: boolean
    filterByStructure?: boolean
    open?: boolean
    dialogOnly?: boolean
    dialogTitle?: string
    error?: boolean
    disabled?: boolean
    onCancel?: () => void
    onSubmit: (response: { supplierId: string; supplierName: string } | null) => void
}

type SupplierStructureItem = { supplierId?: AppId, structureId?: AppId }

const getSupplierDisplayName = (supplier: Supplier) => supplier.shortName?.trim() || supplier.name?.trim() || ""

export const SuppliersSelector = ({
    selectedStructureId,
    value,
    showFiltersTab,
    filterByStructure = false,
    open: controlledOpen,
    dialogOnly = false,
    dialogTitle = "supplier-selector-title",
    error = false,
    disabled = false,
    onCancel,
    onSubmit,
}: SuppliersSelectorProps) => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch()
    const { locale } = useAppSelector(selectSettings);
    const suppliersState = useAppSelector(selectSuppliers)
    const suppliers = useAppSelector(selectAllSuppliers)
    const normalizedValue = String(value ?? "").trim()

    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [], quickFilterValues: [] });
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const [createOpen, setCreateOpen] = useState(false)
    const [selectedSupplierId, setSelectedSupplierId] = useState<AppId>("")
    const [compatibleSupplierIds, setCompatibleSupplierIds] = useState<Set<AppId> | null>(null)
    const [loadingStructures, setLoadingStructures] = useState(false)
    const [structureCompatibleOnly, setStructureCompatibleOnly] = useState(filterByStructure ?? false)
    const [selection, setSelection] = useState<GridRowSelectionModel>(normalizedValue ? [normalizedValue] : []);
    const isDialogOpen = controlledOpen ?? uncontrolledOpen

    const setDialogOpen = useCallback((nextOpen: boolean) => {
        if (disabled && nextOpen) {
            return
        }
        if (controlledOpen === undefined) {
            setUncontrolledOpen(nextOpen)
        }
    }, [controlledOpen, disabled])

    const selectedSupplier = useMemo(
        () => suppliers.find((supplier) => String(supplier.id) === normalizedValue)
            ?? suppliers.find((supplier) => getSupplierDisplayName(supplier) === value)
            ?? null,
        [normalizedValue, suppliers, value]
    )

    const localeText = useMemo(() => {
            const en = enUS.components.MuiDataGrid.defaultProps.localeText;
            return locale === "pl"
                ? plPL.components.MuiDataGrid.defaultProps.localeText
                : en;
        }, [locale]);

    const visibleSuppliers = useMemo(() => {
        let rows = suppliers;
        const hasFilterStructureId = showFiltersTab && selectedStructureId !== undefined && selectedStructureId !== null;
        if (showFiltersTab && structureCompatibleOnly && hasFilterStructureId && compatibleSupplierIds) {
            rows = rows.filter((supplier) => compatibleSupplierIds.has(supplier.id));
        }
        return rows;
    }, [suppliers, showFiltersTab, structureCompatibleOnly, selectedStructureId, compatibleSupplierIds]);

    useEffect(() => {
        if (isDialogOpen && (suppliersState.state.type === "empty" || suppliersState.state.type === "error")) {
            void dispatch(loadSuppliers())
        }
    }, [dispatch, isDialogOpen, suppliersState.state.type])

    useEffect(() => {
        if (isDialogOpen) {
            setSelectedSupplierId(selectedSupplier?.id ?? "")
            setSelection(selectedSupplier?.id ? [String(selectedSupplier.id)] : [])
            return
        }

        setSelectedSupplierId("")
        setSelection([])
        setCompatibleSupplierIds(null)
        setLoadingStructures(false)
        setStructureCompatibleOnly(filterByStructure)
    }, [isDialogOpen, selectedSupplier?.id, filterByStructure])

    useEffect(() => {
        if (!isDialogOpen || !structureCompatibleOnly || !selectedStructureId) {
            setCompatibleSupplierIds(null)
            return
        }

        let cancelled = false

        const loadStructureCompatibility = async () => {
            setLoadingStructures(true)
            try {
                const items = await dispatch(loadStructureSuppliers(selectedStructureId)).unwrap()
                const ids = new Set(
                    (items as SupplierStructureItem[])
                        .map((item) => item.supplierId)
                        .filter((id): id is AppId => Boolean(id))
                )

                if (!cancelled) {
                    setCompatibleSupplierIds(ids)
                }
            } finally {
                if (!cancelled) {
                    setLoadingStructures(false)
                }
            }
        }

        void loadStructureCompatibility()

        return () => {
            cancelled = true
        }
    }, [dispatch, isDialogOpen, structureCompatibleOnly, selectedStructureId])

    const sortedSuppliers = useMemo(
        () => [...suppliers].sort((left, right) => getSupplierDisplayName(left).localeCompare(getSupplierDisplayName(right))),
        [suppliers]
    )

    const filteredSuppliers = useMemo(() => {
        if (!structureCompatibleOnly || !selectedStructureId || !compatibleSupplierIds) {
            return sortedSuppliers
        }

        return sortedSuppliers.filter((supplier) => compatibleSupplierIds.has(supplier.id))
    }, [structureCompatibleOnly, selectedStructureId, sortedSuppliers, compatibleSupplierIds])

    useEffect(() => {
        if (!structureCompatibleOnly || !selectedSupplierId) {
            return
        }

        if (filteredSuppliers.some((supplier) => supplier.id === selectedSupplierId)) {
            return
        }

        setSelectedSupplierId("")
    }, [filteredSuppliers, structureCompatibleOnly, selectedSupplierId])

    const selectedSupplierInDialog = useMemo(
        () => filteredSuppliers.find((supplier) => supplier.id === selectedSupplierId) ?? null,
        [filteredSuppliers, selectedSupplierId]
    )

    const displayName = selectedSupplier?.shortName?.trim() || selectedSupplier?.name?.trim() || String(value ?? "").trim()

    const handleConfirm = useCallback(() => {
        if (disabled || !selectedSupplierInDialog) {
            return
        }

        const response = {
            supplierId: selectedSupplierInDialog.id,
            supplierName: getSupplierDisplayName(selectedSupplierInDialog),
        }

        onSubmit(response)
        setDialogOpen(false)
    }, [disabled, onSubmit, selectedSupplierInDialog, setDialogOpen])

    const handleClear = useCallback(() => {
        if (disabled) {
            return
        }
        onSubmit(null)
    }, [disabled, onSubmit])

    const handleCreateSupplierSuccess = useCallback((supplier: Supplier) => {
        const supplierId = String(supplier.id)
        setSelectedSupplierId(supplier.id)
        setSelection([supplierId])
        setCreateOpen(false)
        setCompatibleSupplierIds((current) => {
            if (!current) {
                return current
            }
            const next = new Set(current)
            next.add(supplier.id)
            return next
        })
    }, [])

    const quickFilterTerms = useMemo(
        () => (filterModel.quickFilterValues ?? [])
            .map((v) => String(v).trim())
            .filter(Boolean),
        [filterModel.quickFilterValues]
    );

    const renderHighlightedText = useCallback((value: unknown) => {
        const text = String(value ?? "");
        if (!text || quickFilterTerms.length === 0) {
            return <>{text}</>;
        }
        const escapedTerms = quickFilterTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
        const regex = new RegExp(`(${escapedTerms.join("|")})`  , "ig");
        const parts = text.split(regex);
        return (
            <>
                {parts.map((part, idx) => {
                    const isMatch = quickFilterTerms.some((term) => term.toLowerCase() === part.toLowerCase());
                    return isMatch ? (
                        <Box key={`${part}-${idx}`} component="mark">
                            <mark><b> {part}</b></mark>
                        </Box>
                    ) : (
                        <span key={`${part}-${idx}`}>{part}</span>
                    );
                })}
            </>
        );
    }, [quickFilterTerms]);

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: "shortName",
                headerName: l10n.getString("supplier-shortName", null, "_Skrócona nazwa"),
                width: 200,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "name",
                headerName: l10n.getString("supplier-name", null, "_Nazwa"),
                minWidth: 250,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "email",
                headerName: l10n.getString("supplier-email", null, "_Email"),
                width: 300,
                renderCell: (params) => renderHighlightedText(params.value),
            },
            {
                field: "supplierType",
                headerName: l10n.getString("supplier-type", null, "_Typ dostawcy"),
                minWidth: 200,
            },
        ],
        [l10n, renderHighlightedText]
    );

    return (
        <>
            {!dialogOnly && (
            <Stack spacing={0.75}>
                {/* <Stack direction="row" alignItems="center" spacing={0.75}>
                    {value ? (
                        <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} aria-hidden />
                    ) : (
                        <PriorityHighIcon sx={{ color: error ? "error.main" : "warning.main", fontSize: 18 }} aria-hidden />
                    )}
                    <Typography variant="subtitle2" fontWeight={700} color={error ? "error" : "text.primary"}>
                        {value ? (
                            <Localized id="supplier-selector-selected">
                                <span>_Wybrany dostawca</span>
                            </Localized>
                        ) : (
                            <Localized id="supplier-selector-title">
                                <span>_Wybierz dostawcę</span>
                            </Localized>
                        )}
                    </Typography>
                </Stack> */}

                <Paper
                    variant="outlined"
                    role="button"
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    onClick={() => setDialogOpen(true)}
                    onKeyDown={(event) => {
                        if (!disabled && (event.key === "Enter" || event.key === " ")) {
                            event.preventDefault()
                            setDialogOpen(true)
                        }
                    }}
                    sx={(theme) => ({
                        p: 1.25,
                        borderRadius: 1.5,
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.6 : 1,
                        borderColor: error ? theme.palette.error.main : theme.palette.divider,
                        "&:hover": {
                            borderColor: disabled ? theme.palette.divider : theme.palette.text.primary,
                        },
                        "&:focus-visible": {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: 2,
                        },
                    })}
                >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                <Localized id="supplier">
                                    <span>_Dostawca</span>
                                </Localized>
                            </Typography>
                            <Typography variant="subtitle2" fontWeight={700} color={value ? "text.primary" : "text.disabled"}>
                                {displayName || "—"}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                            {value && (
                                <Tooltip title={<Localized id="clear-selected"><span>_Wyczyść wybór</span></Localized>}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label="Wyczyść wybór"
                                            disabled={disabled}
                                            onClick={(event) => {
                                                event.stopPropagation()
                                                handleClear()
                                            }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                            <Tooltip title={<Localized id="select"><span>_Wybierz</span></Localized>}>
                                <IconButton size="small" aria-label="Wybierz dostawcę" disabled={disabled} onClick={() => setDialogOpen(true)}>
                                    <SearchIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </Paper>

                {error && (
                    <Box sx={{ mt: 0.25, color: "error" }}>
                        <AppRequiredMessage />
                    </Box>
                )}
            </Stack>
            )}

            <Dialog open={isDialogOpen && !disabled} fullWidth maxWidth="lg" onClose={() => setDialogOpen(false)}>
                <DialogTitle>
                    <Localized id={dialogTitle}>
                        <span>_Wybierz dostawcę</span>
                    </Localized>
                </DialogTitle>

                <DialogContent dividers>
                    {suppliersState.state.type === "loading" || loadingStructures ? (
                        <Skeleton variant="rectangular" height="60vh" />
                    ) : (
                        <Stack spacing={2}>
                            {showFiltersTab && (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 1.5,
                                        backgroundColor: (theme) => theme.palette.grey[50],
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={0.75}
                                        alignItems={{ xs: "flex-start", md: "center" }}
                                        justifyContent="space-between"
                                    >
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                {l10n.getString("filters", null, "Filters")}
                                            </Typography>
                                        </Box>

                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            spacing={1.5}
                                            useFlexGap
                                            flexWrap="wrap"
                                            sx={{ columnGap: 2, rowGap: 0.5 }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={structureCompatibleOnly}
                                                        onChange={(_, checked) => setStructureCompatibleOnly(checked)}
                                                        color="primary"
                                                    />
                                                }
                                                label={l10n.getString("urs-filter-structure-compatible", null, "Only matching structure")}
                                                sx={{ mr: 0, my: 0 }}
                                            />
                                        </Stack>
                                    </Stack>
                                </Paper>
                            )}

                            <Box sx={{ height: "60vh", minHeight: 300, width: "100%" }}>
                                <DataGrid
                                    rows={visibleSuppliers}
                                    columns={columns}
                                    localeText={localeText}
                                    density="compact"
                                    filterModel={filterModel}
                                    onFilterModelChange={setFilterModel}
                                    rowSelectionModel={selection}
                                    onRowSelectionModelChange={(model) => {
                                        const id = model.length ? String(model[0]) : "";
                                        setSelection(id ? [id] : []);
                                        setSelectedSupplierId(id);
                                    }}
                                    slots={{ toolbar: QuickSearchToolbar }}
                                    initialState={{
                                        sorting: { sortModel: [{ field: "title", sort: "asc" }] },
                                    }}
                                />
                            </Box>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={disabled} onClick={() => setCreateOpen(true)}>
                        <Localized id="supplier-selector-create-new">
                            <span>_Utwórz nowego dostawcę</span>
                        </Localized>
                    </Button>
                    <Button size="small" variant="contained" onClick={handleConfirm} disabled={disabled || !selectedSupplierInDialog}>
                        <Localized id="select">
                            <span>_Wybierz</span>
                        </Localized>
                    </Button>
                    <CancelButton loading={false} onClick={() => { setDialogOpen(false); onCancel?.(); }} />
                </DialogActions>
            </Dialog>

            <Dialog open={createOpen && !disabled} fullWidth maxWidth="md" onClose={() => setCreateOpen(false)}>
                <CreateSupplierDialog
                    structureId={selectedStructureId ?? undefined}
                    onSuccess={handleCreateSupplierSuccess}
                    onCancel={() => setCreateOpen(false)}
                />
            </Dialog>
        </>
    )
}